import type { SexAtBirth } from "@src/packages/experience";
import type { TreatmentGoal } from "@src/packages/patients/patients.types";

export type SexAtBirthGoalImpactKind = "none" | "recalculate" | "review";

export type SexAtBirthGoalImpact = {
  kind: SexAtBirthGoalImpactKind;
  goal: TreatmentGoal | null;
  requiredMaterial: "egg" | "sperm" | null;
};

function requiredMaterialForGoal(
  goal: TreatmentGoal | null | undefined,
): "egg" | "sperm" | null {
  switch (goal) {
    case "egg-freezing":
    case "egg-preserve":
    case "egg-donation":
      return "egg";
    case "sperm-freezing":
    case "sperm-preserve":
    case "sperm-donation":
      return "sperm";
    default:
      return null;
  }
}

/**
 * Classifies the user-visible impact of changing sex at birth.
 *
 * This never changes the goal. It only identifies whether the current goal
 * can stay, needs its derived context recalculated, or needs an explicit user
 * review because its self-provided material no longer matches the new profile
 * context.
 */
export function resolveSexAtBirthGoalImpact(
  goal: TreatmentGoal | null | undefined,
  nextSexAtBirth: SexAtBirth | null | undefined,
): SexAtBirthGoalImpact {
  const normalizedGoal = goal ?? null;
  const requiredMaterial = requiredMaterialForGoal(normalizedGoal);

  if (!normalizedGoal || normalizedGoal === "exploring") {
    return { kind: "none", goal: normalizedGoal, requiredMaterial };
  }

  const hasOppositeSelfMaterial =
    (requiredMaterial === "egg" && nextSexAtBirth === "AMAB") ||
    (requiredMaterial === "sperm" && nextSexAtBirth === "AFAB");

  if (hasOppositeSelfMaterial) {
    return { kind: "review", goal: normalizedGoal, requiredMaterial };
  }

  return { kind: "recalculate", goal: normalizedGoal, requiredMaterial };
}
